import { mean, deviation } from "d3-array";
import { isNotDefined, getSourcePathAccessor } from "../utils";
import { vidya as defaultOptions } from "./defaultOptionsForComputation";

/**
 * VIDYA - Variable Index Dynamic Average
 *
 * An adaptive moving average that adjusts its smoothing based on market volatility.
 * Uses standard deviation to measure volatility.
 *
 * Formula:
 * VI = STD(5) / SMA(STD(5), 20)
 * alpha = 2 / (period + 1)
 * VIDYA = (alpha * VI * price) + ((1 - alpha * VI) * previousVIDYA)
 */

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { windowSize, sourcePath } = options;
    const source = getSourcePathAccessor(sourcePath);
    const alpha = 2 / (windowSize + 1);

    // Step 1: Calculate 5-period standard deviation for each point
    const stdPeriod = 5;
    const stdData = [];
    for (let i = 0; i < data.length; i++) {
      if (i < stdPeriod - 1) {
        stdData[i] = undefined;
        continue;
      }
      const values = [];
      for (let j = 0; j < stdPeriod; j++) {
        const val = source(data[i - j]);
        if (!isNotDefined(val)) {
          values.push(val);
        }
      }
      if (values.length === stdPeriod) {
        stdData[i] = deviation(values);
      } else {
        stdData[i] = undefined;
      }
    }

    // Step 2: Calculate 20-period SMA of the standard deviation
    const maPeriod = 20;
    const maStdData = [];
    for (let i = 0; i < data.length; i++) {
      if (i < stdPeriod + maPeriod - 2) {
        maStdData[i] = undefined;
        continue;
      }
      const stdValues = [];
      for (let j = 0; j < maPeriod; j++) {
        const std = stdData[i - j];
        if (!isNotDefined(std)) {
          stdValues.push(std);
        }
      }
      if (stdValues.length === maPeriod) {
        maStdData[i] = mean(stdValues);
      } else {
        maStdData[i] = undefined;
      }
    }

    // Step 3: Calculate VIDYA
    let previousVIDYA;
    const minIndex = Math.max(windowSize - 1, stdPeriod + maPeriod - 2);

    return data.map((d, i) => {
      const v = source(d, i);
      const std = stdData[i];
      const maStd = maStdData[i];

      // Skip undefined values at start
      if (isNotDefined(previousVIDYA) && isNotDefined(v)) {
        return undefined;
      }

      // Need both STD and MA(STD) to calculate VI, and must be past minimum bars
      if (
        i < minIndex ||
        isNotDefined(std) ||
        isNotDefined(maStd) ||
        maStd === 0
      ) {
        return undefined;
      }

      // Initialize with SMA for first valid point
      if (isNotDefined(previousVIDYA)) {
        // Calculate initial SMA
        let sum = 0;
        let count = 0;
        for (let j = 0; j < windowSize; j++) {
          const val = source(data[i - j]);
          if (!isNotDefined(val)) {
            sum += val;
            count++;
          }
        }
        if (count === windowSize) {
          previousVIDYA = sum / windowSize;
          return previousVIDYA;
        }
        return undefined;
      }

      // Calculate Volatility Index (VI)
      const vi = std / maStd;

      // Calculate VIDYA using adaptive alpha
      const adaptiveAlpha = alpha * vi;
      const nextValue = adaptiveAlpha * v + (1 - adaptiveAlpha) * previousVIDYA;
      previousVIDYA = nextValue;

      return nextValue;
    });
  }

  calculator.undefinedLength = function () {
    const { windowSize } = options;
    // Need max of windowSize and (5 + 20 - 1) for STD calculation
    return Math.max(windowSize - 1, 23);
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
